<script setup lang="ts">
    import { computed, onMounted, ref, type Ref, watch } from 'vue'
    import { useRoute } from 'vue-router'

    import moment from 'moment'

    import { generateName } from '@common/Common'
    import { guid } from '@common/Guid'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'

    import { useScenariosPageQueryV1 } from '@Quest/CorporateScenarios/queries-vue-composables'

    import {
        useCloneCorporateScenarioCommandV1,
        useCreateCorporateScenarioCommandV1,
        useDeleteCorporateScenarioCommandV1,
        useUpdateCorporateScenarioCommandV1
    } from '@Quest/CorporateScenarios/commands-vue-composables'

    import type {
        CorporateScenarioReadModel,
        ScenariosPageReadModel
    } from '@Quest/CorporateScenarios/readmodels'

    // ======================
    // Route params
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    // ======================
    // Query
    // ======================

    const {
        runScenariosPageQueryV1,
        modelScenariosPageQueryV1
    } = useScenariosPageQueryV1(workspaceId, strategyId)

    const rm = modelScenariosPageQueryV1 as Ref<ScenariosPageReadModel | null>

    const scenarioIdToDelete = ref<string | null>(null)

    // ======================
    // Commands
    // ======================

    const {
        invokeCloneCorporateScenarioCommandV1,
        succeedCloneCorporateScenarioCommandV1
    } = useCloneCorporateScenarioCommandV1(workspaceId, strategyId)

    const {
        invokeCreateCorporateScenarioCommandV1,
        succeedCreateCorporateScenarioCommandV1
    } = useCreateCorporateScenarioCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateCorporateScenarioCommandV1,
        succeedUpdateCorporateScenarioCommandV1
    } = useUpdateCorporateScenarioCommandV1(workspaceId, strategyId)

    const {
        invokeDeleteCorporateScenarioCommandV1,
        succeedDeleteCorporateScenarioCommandV1
    } = useDeleteCorporateScenarioCommandV1(workspaceId, strategyId, scenarioIdToDelete)



    // ======================
    // Formatting
    // ======================

    function formatDate(date: Date | null)
    {
        if (date == null) return null
        return moment(date).format('MMM D, YYYY')
    }

    function formatTime(date: Date | null)
    {
        if (date == null) return null
        return moment(date).format('LT')
    }

    // ======================
    // Handlers
    // ======================

    function handleCloneCorporateScenarioCommand(s: CorporateScenarioReadModel)
    {
        invokeCloneCorporateScenarioCommandV1({
            Id: guid(),
            SourceId: s.Id,
            CloneName: generateName(s.Name)
        })
    }

    function handleDeleteCorporateScenarioCommand(scenarioId: string)
    {
        if (confirm('Are you sure?') === true)
        {
            scenarioIdToDelete.value = scenarioId
            invokeDeleteCorporateScenarioCommandV1()
        }
    }

    // ======================
    // Command Success Watchers
    // ======================

    watch(succeedCreateCorporateScenarioCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            runScenariosPageQueryV1()
        }
    })

    watch(succeedUpdateCorporateScenarioCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            runScenariosPageQueryV1()
        }
    })

    watch(succeedCloneCorporateScenarioCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            runScenariosPageQueryV1()
        }
    })

    watch(succeedDeleteCorporateScenarioCommandV1, (current, previous) =>
    {
        if (current && !previous && rm.value && scenarioIdToDelete.value)
        {
            rm.value.Scenarios = rm.value.Scenarios.filter(a => a.Id !== scenarioIdToDelete.value)
            scenarioIdToDelete.value = null
        }
    })

    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = 'Enterprise Plans'
        runScenariosPageQueryV1()
    })
</script>

<template>
    <div>
        <CorporateNavigation ActivePage="dashboard"></CorporateNavigation>

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Enterprise plans</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <a class="btn btn-success mr-1" style="padding: 0.7em 2em;" v-sanitizedHref="`/#/enterprise-plan/settings/${workspaceId}/${strategyId}`">Create plan</a>
                    <a class="btn btn-lg btn-outline-secondary" href="https://www.transparentchoice.com/client-hub/user-guide/scenarios" title="Documentation" target="_blank">
                        <i class="fa fa-info" aria-hidden="true"></i>
                    </a>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="row">
                <div class="col-lg-12">
                    <div>
                        <table class="table table-bordered table-hover">
                            <thead>
                                <tr>
                                    <th class="text-center" style="width: 120px;">Created</th>
                                    <th>Plan</th>
                                    <th class="text-center" style="width: 240px;">Planning horizon</th>
                                    <th class="text-center" style="width: 120px;">Portfolios</th>
                                    <th style="width: 120px;"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="s in rm.Scenarios" :key="s.Id">
                                    <td class="text-center">
                                        {{formatDate(s.CreatedDate)}}<br />
                                        <small>{{formatTime(s.CreatedDate)}}</small>
                                    </td>
                                    <td>
                                        <a v-sanitizedHref="`/#/enterprise-plan/portfolios/${workspaceId}/${strategyId}/${s.Id}`">{{s.Name}}</a><br />
                                        <small>{{s.Description}}</small>
                                    </td>

                                    <td class="text-center">
                                        {{formatDate(s.PlanningStartDate)}} - {{formatDate(s.PlanningEndDate)}}
                                    </td>
                                    <td class="text-center">
                                        {{s.Portfolios}}
                                    </td>
                                    <td>
                                        <div class="dropdown">
                                            <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                                Actions
                                            </button>
                                            <div class="dropdown-menu dropdown-menu-right">
                                                <a class="dropdown-item" v-sanitizedHref="`/#/enterprise-plan/portfolios/${workspaceId}/${strategyId}/${s.Id}`">View</a>
                                                <a class="dropdown-item" v-sanitizedHref="`/#/enterprise-plan/settings/${workspaceId}/${strategyId}/${s.Id}`">Settings</a>
                                                <a class="dropdown-item" @click="handleCloneCorporateScenarioCommand(s)">Clone</a>
                                                <div class="dropdown-divider"></div>
                                                <a class="dropdown-item" @click="handleDeleteCorporateScenarioCommand(s.Id)">Delete</a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                </div>
            </div>
        </div>

    </div>
</template>


